import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Camera, Trash, PaperPlaneTilt, CheckCircle, WarningCircle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/scan`;

// Öffentliche Handy-Seite: Seiten fotografieren und an den PC übertragen.
export default function HandyScan() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/${token}`)
      .then(async (r) => (r.ok ? setInfo(await r.json()) : setError((await r.json()).detail || "Link ungültig")))
      .catch(() => setError("Verbindung fehlgeschlagen"));
  }, [token]);

  const addPhotos = (files) => {
    const list = Array.from(files || []).map((f) => ({ file: f, url: URL.createObjectURL(f) }));
    setPhotos((p) => [...p, ...list]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const send = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      photos.forEach((p) => fd.append("files", p.file));
      const r = await fetch(`${API}/${token}/upload`, { method: "POST", body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Übertragung fehlgeschlagen");
      setDone(data);
    } catch (e) {
      setError(e.message);
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <Shell>
        <CheckCircle size={56} weight="fill" className="text-emerald-600 mx-auto" />
        <h1 className="text-xl font-semibold text-center mt-3" data-testid="handy-scan-done">Übertragen</h1>
        <p className="text-sm text-slate-600 text-center mt-1">{done.pages} Seite{done.pages === 1 ? "" : "n"} wurden an den PC gesendet. Sie können diese Seite schließen.</p>
      </Shell>
    );
  }
  if (error && !info) {
    return (
      <Shell>
        <WarningCircle size={56} className="text-rose-600 mx-auto" />
        <p className="text-center mt-3 text-slate-800" data-testid="handy-scan-error">{error}</p>
      </Shell>
    );
  }
  return (
    <Shell>
      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Unterschriebenes Original</div>
      <h1 className="text-2xl font-semibold text-slate-900 mt-1">Seiten fotografieren</h1>
      {info?.label && <p className="text-sm text-slate-600 mt-1" data-testid="handy-scan-label">{info.label}</p>}
      <p className="text-xs text-slate-500 mt-2">Jede Seite einzeln, gerade und gut beleuchtet aufnehmen. Die Reihenfolge entspricht der Seitenfolge.</p>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" multiple className="hidden"
             onChange={(e) => addPhotos(e.target.files)} data-testid="handy-scan-input" />
      <button type="button" onClick={() => inputRef.current?.click()}
              className="mt-5 w-full h-14 rounded-xl bg-slate-900 text-white font-medium flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
              data-testid="handy-scan-capture">
        <Camera size={22} /> {photos.length ? "Weitere Seite aufnehmen" : "Erste Seite aufnehmen"}
      </button>
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mt-4" data-testid="handy-scan-thumbs">
          {photos.map((p, i) => (
            <div key={p.url} className="relative rounded-lg overflow-hidden border border-slate-200 aspect-[3/4] bg-slate-100">
              <img src={p.url} alt={`Seite ${i + 1}`} className="w-full h-full object-cover" />
              <span className="absolute top-1 left-1 text-[10px] bg-white/90 rounded px-1">{i + 1}</span>
              <button type="button" onClick={() => setPhotos((s) => s.filter((_, j) => j !== i))}
                      className="absolute top-1 right-1 bg-white/90 rounded p-1 text-rose-700" data-testid={`handy-scan-remove-${i}`}>
                <Trash size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
      {error && <p className="text-sm text-rose-700 mt-3">{error}</p>}
      <button type="button" disabled={!photos.length || busy} onClick={send}
              className="mt-5 w-full h-14 rounded-xl bg-emerald-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-40"
              data-testid="handy-scan-send">
        <PaperPlaneTilt size={20} /> {busy ? "Überträgt…" : `Fertig – übertragen${photos.length ? ` (${photos.length})` : ""}`}
      </button>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-stone-50 px-5 py-8">
      <div className="max-w-md mx-auto">{children}</div>
    </div>
  );
}
